import { HttpErrorResponse, HttpHeaders, HttpInterceptorFn, HttpStatusCode } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthStore } from '@auth';
import { MsalService } from '@azure/msal-angular';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { SilentRequest } from '@azure/msal-browser/src/request/SilentRequest';
import { AppConfig } from '@common/tokens/app-configs.constant';
import { ToastService } from '@ui';
import { catchError, EMPTY, from, switchMap, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';

import { skipToastOnErrorContext } from '../context/skip-toast-on-error.context';

export const httpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const msalAuthService = inject(MsalService);
  const authStore = inject(AuthStore);
  const appConfig = inject(AppConfig);
  const toastService = inject(ToastService);

  return next(req).pipe(
    catchError((httpErrorResponse: HttpErrorResponse) => {
      switch (httpErrorResponse.status) {
        case HttpStatusCode.Unauthorized: {
          const activeAccount = msalAuthService.instance.getActiveAccount();
          if (activeAccount) {
            const silentRequest: SilentRequest = {
              account: activeAccount,
              scopes: [...appConfig.msalConfig.api.scopes],
              forceRefresh: true
            };

            return from(msalAuthService.instance.acquireTokenSilent(silentRequest)).pipe(
              tap(data => authStore.updateIdToken(data.idToken)),
              switchMap(data =>
                next(req.clone({ headers: new HttpHeaders({ Authorization: `Bearer ${data.idToken}` }) }))
              ),
              catchError(error => {
                if (error instanceof HttpErrorResponse) {
                  return throwError(() => error);
                }

                if (error instanceof InteractionRequiredAuthError) {
                  return msalAuthService.acquireTokenRedirect({ scopes: [...appConfig.msalConfig.api.scopes] }).pipe(
                    catchError(() => {
                      authStore.clearAuthInfo();
                      msalAuthService.logoutRedirect();
                      return EMPTY;
                    }),
                    switchMap(() => {
                      const activeAccount = msalAuthService.instance.getActiveAccount();
                      authStore.updateIdToken(activeAccount?.idToken);
                      return next(
                        req.clone({ headers: new HttpHeaders({ Authorization: `Bearer ${activeAccount?.idToken}` }) })
                      );
                    })
                  );
                }

                authStore.clearAuthInfo();
                msalAuthService.logoutRedirect();
                return EMPTY;
              })
            );
          }

          return throwError(() => ({
            status: httpErrorResponse.status,
            title: 'Error',
            message: 'Unauthorized'
          }));
        }
        case HttpStatusCode.BadRequest: {
          return throwError(() => ({
            status: httpErrorResponse.status,
            title: 'Bad Request',
            message: httpErrorResponse.error?.message || 'Bad request',
            url: httpErrorResponse.url,
            ...httpErrorResponse.error
          }));
        }
        default: {
          return throwError(() => ({
            status: httpErrorResponse.status,
            title: `Error`,
            message: httpErrorResponse.error?.message || 'Something went wrong',
            url: httpErrorResponse.url
          }));
        }
      }
    }),
    catchError(error => {
      if (!req.context.get(skipToastOnErrorContext)) {
        if (!error.status) {
          toastService.showError('Network Error', 'There was an error with your network request.');
        }
        if (!!error.status) {
          toastService.showError(error.title, error.message);
        }
      }
      return throwError(() => error);
    })
  );
};
